export type VideoType = 'youtube' | 'vimeo' | 'dailymotion' | 'wistia' | 'wistia-embeded';

export interface Video {
  id: string;
  url: string;
  type: VideoType;
}

export function parseVideo(url: string): Video | null {
  const videoType = detectVideoType(url);
  if (!videoType) return null;

  let videoId: string;
  let videoURL: string;
  const params = new URL(url).search;

  switch (videoType) {
    case 'youtube':
      videoId = extractYouTubeID(url);
      videoURL = `https://www.youtube.com/embed/${videoId}`;
      break;
    case 'vimeo':
      videoId = extractVimeoID(url);
      // params is needed for vimeo since it may contain a hash needed for embedding
      // https://help.vimeo.com/hc/en-us/articles/12426470858001-Embedded-player-displays-This-video-does-not-exist-message
      videoURL = `https://player.vimeo.com/video/${videoId}` + params;
      break;
    case 'dailymotion':
      videoId = extractDailymotionID(url);
      videoURL = `https://www.dailymotion.com/embed/video/${videoId}?color=ffffff&title=0&byline=0&portrait=0`;
      break;
    case 'wistia':
      videoId = extractWistiaID(url);
      videoURL = `https://fast.wistia.net/embed/iframe/${videoId}`;
      break;
    case 'wistia-embeded':
      videoId = extractEmbeddedWistiaID(url);
      videoURL = url; // already an embedded video
      break;
    default:
      return null;
  }

  return {
    id: videoId,
    url: videoURL,
    type: videoType,
  };
}

function extractYouTubeID(url: string): string {
  const regex = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
  return url.match(regex)?.[1] ?? '';
}

function extractVimeoID(url: string): string {
  const regex = /vimeo\.com\/(?:video\/)?(\d+)/;
  return url.match(regex)?.[1] ?? '';
}

function extractDailymotionID(url: string): string {
  const regex = /dailymotion\.com\/(?:embed\/)?video\/([\w-]+)/;
  return url.match(regex)?.[1] ?? '';
}

function extractWistiaID(url: string): string {
  const regex = /wistia\.(?:com|net)\/(?:medias\/)?([\w-]+)/;
  return url.match(regex)?.[1] ?? '';
}

function extractEmbeddedWistiaID(url: string): string {
  const regex = /fast\.wistia\.net\/embed\/iframe\/([\w-]+)/;
  return url.match(regex)?.[1] ?? '';
}

export function detectVideoType(url: string): VideoType | null {
  if (!url) {
    return null;
  }

  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
  const vimeoRegex = /^(https?:\/\/)?(www\.)?(vimeo|player\.vimeo)\.com\/.+$/;
  const dailymotionRegex = /^(https?:\/\/)?(www\.)?dailymotion\.com\/.+$/;
  const wistiaRegex = /https?:\/\/(.+?\.)?(wistia\.com|wi\.st|fast\.wistia\.net\/embed\/iframe)\/[A-Za-z0-9._%-]+/;

  if (youtubeRegex.test(url)) {
    return 'youtube';
  } else if (vimeoRegex.test(url)) {
    return 'vimeo';
  } else if (dailymotionRegex.test(url)) {
    return 'dailymotion';
  } else if (wistiaRegex.test(url)) {
    return url.includes('fast.wistia.net/embed/iframe') ? 'wistia-embeded' : 'wistia';
  }

  return null;
}
